<template>
  <div class="skeleton-wrapper" :class="wrapperClass">
    <!-- Dashboard Skeleton -->
    <template v-if="type === 'dashboard'">
      <div class="skeleton-dashboard">
        <!-- Hero Section Skeleton -->
        <div class="skeleton-hero">
          <div class="skeleton-hero-text">
            <div class="skeleton-line skeleton-sm"></div>
            <div class="skeleton-line skeleton-lg"></div>
          </div>
          <div class="skeleton-balance-grid">
            <div class="skeleton-balance-card" v-for="i in 3" :key="i">
              <div class="skeleton-circle skeleton-icon"></div>
              <div class="skeleton-line skeleton-xs"></div>
              <div class="skeleton-line skeleton-md"></div>
            </div>
          </div>
        </div>
        <!-- Content Skeleton -->
        <div class="skeleton-content-grid">
          <div class="skeleton-section">
            <div class="skeleton-line skeleton-md"></div>
            <div class="skeleton-voucher-grid">
              <div class="skeleton-voucher-card" v-for="i in 3" :key="i"></div>
            </div>
          </div>
          <div class="skeleton-section">
            <div class="skeleton-line skeleton-md"></div>
            <div class="skeleton-transaction-list">
              <div class="skeleton-transaction-item" v-for="i in 3" :key="i"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Transaction History Skeleton -->
    <template v-else-if="type === 'transaction'">
      <div class="skeleton-transaction-page">
        <div class="skeleton-transaction-item" v-for="i in count" :key="i">
          <div class="skeleton-circle skeleton-avatar"></div>
          <div class="skeleton-transaction-info">
            <div class="skeleton-line skeleton-md"></div>
            <div class="skeleton-line skeleton-sm"></div>
          </div>
          <div class="skeleton-line skeleton-price"></div>
        </div>
      </div>
    </template>

    <!-- Voucher Skeleton -->
    <template v-else-if="type === 'voucher'">
      <div class="skeleton-voucher-page" :class="{ 'skeleton-grid': isGrid }">
        <div class="skeleton-voucher-item" v-for="i in count" :key="i">
          <div class="skeleton-circle skeleton-voucher-img"></div>
          <div class="skeleton-voucher-info">
            <div class="skeleton-line skeleton-md"></div>
            <div class="skeleton-line skeleton-sm"></div>
            <div class="skeleton-line skeleton-xs"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Deals Skeleton -->
    <template v-else-if="type === 'deals'">
      <div class="skeleton-deals-page" :class="{ 'skeleton-grid': isGrid }">
        <div class="skeleton-deal-card" v-for="i in count" :key="i">
          <div class="skeleton-deal-cost"></div>
          <div class="skeleton-deal-image"></div>
          <div class="skeleton-deal-info">
            <div class="skeleton-circle skeleton-deal-logo"></div>
            <div class="skeleton-deal-text">
              <div class="skeleton-line skeleton-title-line"></div>
              <div class="skeleton-line skeleton-sub-line"></div>
            </div>
          </div>
          <div class="skeleton-deal-dash"></div>
          <div class="skeleton-deal-footer">
            <div class="skeleton-circle skeleton-status-icon"></div>
            <div class="skeleton-line skeleton-status-line"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Benefit Skeleton -->
    <template v-else-if="type === 'benefit'">
      <div class="skeleton-benefit-page">
        <!-- Mobile Profile Section Skeleton -->
        <div class="skeleton-mobile-profile">
          <div class="skeleton-circle skeleton-avatar-mobile"></div>
          <div class="skeleton-profile-text">
            <div class="skeleton-line skeleton-md"></div>
            <div class="skeleton-line skeleton-sm"></div>
          </div>
        </div>
        <!-- Mobile Tier Card Skeleton -->
        <div class="skeleton-mobile-tier-card">
          <div class="skeleton-tier-to-container">
            <!-- Current Tier Skeleton -->
            <div class="skeleton-tier-col">
              <div class="skeleton-circle skeleton-tier-icon-img"></div>
              <div class="skeleton-tier-title-box">
                <div class="skeleton-line skeleton-tier-name"></div>
                <div class="skeleton-line skeleton-tier-amount"></div>
              </div>
            </div>

            <!-- Next Tier Skeleton -->
            <div class="skeleton-tier-col">
              <div class="skeleton-circle skeleton-tier-icon-img"></div>
              <div class="skeleton-tier-title-box">
                <div class="skeleton-line skeleton-tier-name"></div>
                <div class="skeleton-line skeleton-tier-amount"></div>
              </div>
            </div>
          </div>

          <!-- Progress Bar & Pill Skeleton -->
          <div class="skeleton-tier-progress-section">
            <div class="skeleton-line skeleton-progress-bar-line"></div>
            <div class="skeleton-line skeleton-progress-label-pill"></div>
          </div>
        </div>

        <!-- Benefit Section Header Skeleton -->
        <div class="skeleton-benefit-header">
          <div class="skeleton-line skeleton-section-title"></div>
          <div class="skeleton-circle skeleton-filter-btn"></div>
        </div>

        <!-- Benefits List Skeleton -->
        <div class="skeleton-benefit-list">
          <div class="skeleton-benefit-item" v-for="i in count" :key="i">
            <div class="skeleton-circle skeleton-benefit-icon"></div>
            <div class="skeleton-benefit-info">
              <div class="skeleton-line skeleton-md"></div>
              <div class="skeleton-line skeleton-sm"></div>
            </div>
            <div class="skeleton-benefit-arrow"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Benefit Desktop (with top cards) -->
    <template v-else-if="type === 'benefit-desktop'">
      <div class="skeleton-benefit-desktop">
        <!-- Top Cards Row -->
        <div class="skeleton-top-cards">
          <!-- Profile Card -->
          <div class="skeleton-profile-card">
            <div class="skeleton-circle skeleton-avatar-large"></div>
            <div class="skeleton-profile-info">
              <div class="skeleton-line skeleton-md"></div>
              <div class="skeleton-line skeleton-sm"></div>
            </div>
          </div>
          <!-- Tier Card -->
          <div class="skeleton-tier-card">
            <div class="skeleton-line skeleton-sm"></div>
            <div class="skeleton-tier-progress">
              <div class="skeleton-line skeleton-lg"></div>
              <div class="skeleton-line skeleton-md"></div>
            </div>
          </div>
          <!-- Filter Card -->
          <div class="skeleton-filter-card">
            <div class="skeleton-line skeleton-sm"></div>
            <div class="skeleton-input"></div>
            <div class="skeleton-button-group">
              <div class="skeleton-button"></div>
              <div class="skeleton-button"></div>
            </div>
          </div>
        </div>
        <!-- Benefits List -->
        <div class="skeleton-benefits-grid">
          <div class="skeleton-benefit-item" v-for="i in 4" :key="i">
            <div class="skeleton-circle skeleton-benefit-icon"></div>
            <div class="skeleton-benefit-info">
              <div class="skeleton-line skeleton-md"></div>
              <div class="skeleton-line skeleton-sm"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Benefit Accordion Skeleton -->
    <template v-else-if="type === 'benefit-accordion'">
      <div class="skeleton-benefit-accordion-list">
        <div v-for="n in count" :key="n" class="skeleton-accordion-row">
          <div class="skeleton-accordion-header">
            <div class="skeleton-circle skeleton-accordion-icon"></div>
            <div class="skeleton-line skeleton-accordion-title"></div>
            <div class="skeleton-line skeleton-accordion-count"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Account/Form Skeleton -->
    <template v-else-if="type === 'account'">
      <div class="skeleton-account-page">
        <div class="skeleton-avatar-section">
          <div class="skeleton-circle skeleton-avatar-large"></div>
          <div class="skeleton-avatar-options">
            <div class="skeleton-line skeleton-sm"></div>
            <div class="skeleton-avatar-row">
              <div class="skeleton-circle skeleton-avatar-small" v-for="i in 2" :key="i"></div>
            </div>
          </div>
        </div>
        <div class="skeleton-form-section">
          <div class="skeleton-form-row" v-for="i in 4" :key="i">
            <div class="skeleton-form-group" v-for="j in 2" :key="j">
              <div class="skeleton-line skeleton-xs"></div>
              <div class="skeleton-input"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="type === 'point-banner'">
      <div class="skeleton-point-banner" :class="{ 'mobile': isMobile }">
        <div class="skeleton-banner-left">
          <div class="skeleton-line skeleton-point-amount"></div>
          <div class="skeleton-line skeleton-banner-desc"></div>
        </div>
      </div>
    </template>

    <!-- Generic Card Skeleton -->
    <template v-else>
      <div class="skeleton-generic">
        <div class="skeleton-card" v-for="i in count" :key="i">
          <div class="skeleton-line skeleton-lg"></div>
          <div class="skeleton-line skeleton-md"></div>
          <div class="skeleton-line skeleton-sm"></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SkeletonLoader',
  props: {
    type: {
      type: String,
      default: 'generic',
      validator: (value) => ['dashboard', 'transaction', 'voucher', 'deals', 'benefit', 'benefit-desktop', 'benefit-accordion', 'account', 'point-banner', 'generic'].includes(value)
    },
    count: {
      type: Number,
      default: 3
    },
    isGrid: {
      type: Boolean,
      default: false
    },
    wrapperClass: {
      type: String,
      default: ''
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
/* Base Skeleton Styles */
.skeleton-wrapper {
  width: 100%;
  pointer-events: none;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.skeleton-line,
.skeleton-circle,
.skeleton-input,
.skeleton-voucher-card,
.skeleton-transaction-item,
.skeleton-voucher-item,
.skeleton-deal-card,
.skeleton-benefit-item,
.skeleton-deal-image {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Line Sizes */
.skeleton-line {
  height: 16px;
}

.skeleton-line.skeleton-xs {
  width: 60px;
  height: 12px;
}

.skeleton-line.skeleton-sm {
  width: 120px;
  height: 14px;
}

.skeleton-line.skeleton-md {
  width: 180px;
  height: 16px;
}

.skeleton-line.skeleton-lg {
  width: 240px;
  height: 24px;
}

.skeleton-line.skeleton-price {
  width: 80px;
  height: 20px;
}

.skeleton-line.skeleton-deal-status {
  width: 100%;
  height: 32px;
  margin-top: 12px;
}

/* Circle Sizes */
.skeleton-circle {
  border-radius: 50%;
}

.skeleton-circle.skeleton-icon {
  width: 48px;
  height: 48px;
}

.skeleton-circle.skeleton-avatar {
  width: 48px;
  height: 48px;
  min-width: 48px;
}

.skeleton-circle.skeleton-avatar-large {
  width: 100px;
  height: 100px;
}

.skeleton-circle.skeleton-avatar-small {
  width: 50px;
  height: 50px;
}

.skeleton-circle.skeleton-voucher-img {
  width: 80px;
  height: 80px;
  min-width: 80px;
}

.skeleton-circle.skeleton-deal-logo {
  width: 40px;
  height: 40px;
  min-width: 40px;
}

.skeleton-circle.skeleton-benefit-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
}

/* Dashboard Skeleton */
.skeleton-dashboard {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.skeleton-hero {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.skeleton-hero-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-balance-grid {
  display: flex;
  gap: 16px;
}

.skeleton-balance-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  flex: 1;
  max-width: 120px;
}

.skeleton-balance-card .skeleton-line {
  width: 60px;
}

.skeleton-balance-card .skeleton-line.skeleton-md {
  width: 80px;
}

.skeleton-content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.skeleton-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-voucher-grid {
  display: flex;
  gap: 16px;
}

.skeleton-voucher-card {
  width: 200px;
  height: 120px;
  border-radius: 12px;
}

.skeleton-transaction-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Transaction Skeleton */
.skeleton-transaction-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-transaction-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  height: 80px;
}

.skeleton-transaction-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Voucher Skeleton */
.skeleton-voucher-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-voucher-page.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.skeleton-voucher-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 14px;
  background: white;
  border-radius: 12px;
}

.skeleton-voucher-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Deals Skeleton */
.skeleton-deals-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-deals-page.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.skeleton-deal-card {
  background: white;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #E1E4EA;
}

.skeleton-deal-cost {
  position: absolute;
  top: 0;
  left: 0;
  width: 52px;
  height: 22px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px 0 12px 0;
  z-index: 1;
}

.skeleton-deal-image {
  width: 100%;
  height: 140px;
  border-radius: 8px;
}

.skeleton-deal-info {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.skeleton-circle.skeleton-deal-logo {
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-deal-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-line.skeleton-title-line {
  width: 85%;
  height: 14px;
}

.skeleton-line.skeleton-sub-line {
  width: 60%;
  height: 11px;
}

.skeleton-deal-dash {
  border-top: 1px dashed #E1E4EA;
  margin: 2px 0;
  width: 100%;
}

.skeleton-deal-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.skeleton-circle.skeleton-status-icon {
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-line.skeleton-status-line {
  width: 60%;
  height: 11px;
}

/* Benefit Skeleton */
.skeleton-benefit-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-mobile-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.skeleton-circle.skeleton-avatar-mobile {
  width: 96px;
  height: 96px;
  min-width: 96px;
  min-height: 96px;
  border-radius: 50%;
}

.skeleton-profile-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.skeleton-mobile-tier-card {
  background: white;
  border: 1px solid #E1E4EA;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.skeleton-tier-to-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.skeleton-tier-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.skeleton-circle.skeleton-tier-icon-img {
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border-radius: 8px;
}

.skeleton-tier-title-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.skeleton-line.skeleton-tier-name {
  width: 50px;
  height: 14px;
}

.skeleton-line.skeleton-tier-amount {
  width: 80px;
  height: 10px;
}

.skeleton-tier-progress-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.skeleton-line.skeleton-progress-bar-line {
  width: 100%;
  height: 12px;
  border-radius: 6px;
}

.skeleton-line.skeleton-progress-label-pill {
  width: 85px;
  height: 22px;
  border-radius: 11px;
}


.skeleton-benefit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 2px;
}

.skeleton-line.skeleton-section-title {
  width: 140px;
  height: 18px;
}

.skeleton-circle.skeleton-filter-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.skeleton-benefit-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-benefit-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 12px;
}

.skeleton-benefit-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-benefit-arrow {
  width: 24px;
  height: 24px;
  background: #f0f0f0;
  border-radius: 4px;
}

/* Account Skeleton */
.skeleton-account-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.skeleton-avatar-section {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.skeleton-avatar-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-avatar-row {
  display: flex;
  gap: 12px;
}

.skeleton-form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.skeleton-form-row {
  display: flex;
  gap: 12px;
}

.skeleton-form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-input {
  width: 100%;
  height: 44px;
  border-radius: 8px;
}

/* Generic Skeleton */
.skeleton-generic {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Mobile Responsive */
@media (max-width: 900px) {
  .skeleton-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .skeleton-balance-grid {
    width: 100%;
    justify-content: space-between;
  }

  .skeleton-content-grid {
    grid-template-columns: 1fr;
  }

  .skeleton-voucher-grid {
    overflow-x: auto;
  }

  .skeleton-form-row {
    flex-direction: column;
  }

  .skeleton-avatar-section {
    flex-direction: column;
    align-items: center;
  }

  .skeleton-avatar-options {
    align-items: center;
  }

  .skeleton-deals-page.skeleton-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
  }

  .skeleton-deal-card {
    width: 100%;
    box-sizing: border-box;
  }

  .skeleton-deal-image {
    height: 100px;
  }
}

/* Benefit Desktop Skeleton */
.skeleton-benefit-desktop {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}

.skeleton-top-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.skeleton-profile-card,
.skeleton-tier-card,
.skeleton-filter-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-profile-card {
  align-items: center;
  justify-content: center;
}

.skeleton-profile-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.skeleton-tier-progress {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-button-group {
  display: flex;
  gap: 12px;
}

.skeleton-button {
  flex: 1;
  height: 44px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

@media (max-width: 900px) {
  .skeleton-top-cards {
    grid-template-columns: 1fr;
  }

  .skeleton-benefits-grid {
    grid-template-columns: 1fr;
  }
}
.skeleton-point-banner {
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.skeleton-point-banner.mobile {
  border-radius: 12px;
  padding: 16px;
  margin: 16px;
  margin-top: -40px;
  position: relative;
  z-index: 10;
}

.skeleton-banner-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line.skeleton-point-amount {
  width: 150px;
  height: 28px;
}

.skeleton-point-banner.mobile .skeleton-line.skeleton-point-amount {
  width: 120px;
  height: 20px;
}

.skeleton-line.skeleton-banner-desc {
  width: 200px;
  height: 14px;
}

.skeleton-point-banner.mobile .skeleton-line.skeleton-banner-desc {
  width: 160px;
  height: 12px;
}

/* Benefit Accordion Skeleton */
.skeleton-benefit-accordion-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skeleton-accordion-row {
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  overflow: hidden;
}

.skeleton-accordion-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}

.skeleton-circle.skeleton-accordion-icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 8px;
}

.skeleton-line.skeleton-accordion-title {
  flex: 1;
  height: 14px;
  max-width: 200px;
}

.skeleton-line.skeleton-accordion-count {
  width: 70px;
  height: 12px;
  margin-left: auto;
}
</style>
